import React from "react";
import { useNavigate } from "react-router-dom";
import useCartStore from "../api/cart";
import {
  cartGarments,
  cartSubtotal,
  cartServiceCount,
  cartIsCheckoutable,
} from "../utils/cartSummary";

/**
 * The running basket, shown through the intake flow rather than only on the
 * checkout screen. It replaces the old summary box, which showed just the
 * garment being worked on — with a dozen pieces on the counter there was no
 * way to tell what had already gone in.
 *
 * It also carries the way back to checkout. Once an item is added, the
 * type-selection screen's only exits were forward, so changing your mind
 * after "add an item" meant adding a throwaway item and deleting it.
 */
function CartSummary({ isOpen, onToggle, className = "", children }) {
  const navigate = useNavigate();
  const { cartItems } = useCartStore();
  const cart = cartItems?.[0];

  const garments = cartGarments(cart);
  const subtotal = cartSubtotal(cart);
  const count = cartServiceCount(cart);
  const canCheckout = cartIsCheckoutable(cart);

  const money = (amount) => `$${Number(amount || 0).toFixed(2)}`;

  return (
    <div
      className={`show-box cart-summary ${isOpen ? "open" : ""} ${className}`}
    >
      <div className="cart-summary-head" onClick={onToggle}>
        <p className="show-box-para">
          Your basket
          {count > 0
            ? ` · ${count} ${count === 1 ? "service" : "services"}`
            : ""}
        </p>
      </div>

      {/* The piece being worked on right now, which each screen describes in
          its own terms (the task and option being picked, the plan chosen).
          It sits above the basket because it's what the screen is about. */}
      {children && <div className="cart-summary-current">{children}</div>}

      {garments.length === 0 && (
        <h4 className="show-box-answer">Nothing added yet</h4>
      )}

      {garments
        .filter((garment) => !(children && garment.services.length === 0))
        .map((garment) => (
          <div className="show-box-list cart-summary-item" key={garment.id}>
            <h4 className="show-box-answer">{garment.name}</h4>
            {garment.services.length === 0 ? (
              <p className="show-box-para cart-summary-pending">
                Still choosing its services
              </p>
            ) : (
              garment.services.map((service, index) => (
                <div
                  className="flex-box justify-between cart-summary-service"
                  key={`${garment.id}-${index}`}
                >
                  <p className="show-box-para">{service.name}</p>
                  <p className="show-box-para">{money(service.price)}</p>
                </div>
              ))
            )}
          </div>
        ))}

      {subtotal > 0 && (
        <div className="flex-box justify-between cart-summary-total">
          <p className="show-box-para">Subtotal</p>
          <p className="show-box-para">{money(subtotal)}</p>
        </div>
      )}

      {canCheckout && (
        <button
          type="button"
          className="cart-summary-checkout"
          onClick={() => {
            // The flow stashes "am I adding another item?" here; leaving it
            // set makes the next visit to /types reopen in add-item mode.
            sessionStorage.removeItem("addnewitem");
            navigate("/checkout");
          }}
        >
          Back to checkout
        </button>
      )}
    </div>
  );
}

export default CartSummary;
